import { useMutation } from '@tanstack/react-query'

import { authAPI } from '@/api/new-api'

import { useInvalidateIglooDevices } from './get-igloo-devices'
import { IglooKey, normalizeIglooDevices, RawIglooDevices } from './normalize-igloo-devices'

type UpdateIglooDevicesRequest = { clientId: string }

export type UpdateIglooDevicesPayload = {
  // The FULL desired set of door ids for this resident — not a delta. The backend diffs it
  // against the doors the resident currently holds.
  devices: string[]
  expirationDate?: number
}

// Note: the response is the resident's door set AFTER the update, and it is the only signal the
// caller gets about a request that did nothing. A client with no Igloo user answers 200 with an
// empty array rather than an error — verified against staging — so callers must compare what came
// back against what they asked for instead of trusting the status code.
const updateIglooDevices = async (
  data: UpdateIglooDevicesRequest,
  payload: UpdateIglooDevicesPayload,
): Promise<IglooKey[]> => {
  const response = await authAPI.put<RawIglooDevices>(`/igloowork/${data.clientId}/devices`, {
    devices: payload.devices,
    ...(typeof payload.expirationDate === 'number'
      ? { expirationDate: payload.expirationDate }
      : {}),
  })
  return normalizeIglooDevices(response.data).keys
}

export const useUpdateIglooDevices = (data: UpdateIglooDevicesRequest) => {
  const invalidateDevices = useInvalidateIglooDevices(data.clientId)
  return useMutation({
    mutationFn: (payload: UpdateIglooDevicesPayload) => updateIglooDevices(data, payload),
    onSuccess: () => {
      invalidateDevices()
    },
  })
}
